import { useEffect, useState } from "react";

import queryDocument from "@/Apollo/query/promotions/homepagePromotions.graphql";
import type {
  HomepagePromotionsQuery,
  HomepagePromotionsQueryVariables,
} from "@/Apollo/schema";
import { useLanguage } from "@/common/hooks/pages.hooks";
import { useDisplayCurrency } from "@/features/display-currency/useDisplayCurrency";
import { PROMOTIONS_ENABLED } from "@/features/promotions/config";
import { useQuery } from "@apollo/client";

/** Paid presentation never enters Apollo's normalized cache or the homepage ISR payload. */
export const useHomepagePromotions = () => {
  const [mounted, setMounted] = useState(false);
  const { language } = useLanguage();
  const currency = useDisplayCurrency();
  useEffect(() => setMounted(true), []);
  const query = useQuery<
    HomepagePromotionsQuery,
    HomepagePromotionsQueryVariables
  >(queryDocument, {
    variables: { lang: language, currency },
    skip: !mounted || !PROMOTIONS_ENABLED,
    ssr: false,
    fetchPolicy: "no-cache",
    context: { fetchOptions: { cache: "no-store" } },
    notifyOnNetworkStatusChange: true,
  });
  const { refetch } = query;
  useEffect(() => {
    if (!mounted || !PROMOTIONS_ENABLED) {
      return;
    }

    const refreshVisible = () => {
      if (document.visibilityState === "visible") {
        void Promise.resolve(refetch()).catch(() => undefined);
      }
    };
    // Recheck availability on return and at the rotation boundary, before tickets expire.
    const timer = window.setInterval(refreshVisible, 15 * 60_000);
    document.addEventListener("visibilitychange", refreshVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshVisible);
    };
  }, [mounted, refetch]);
  return query;
};
